import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ProviderShell } from "@/components/provider/ProviderShell";
import { ProviderConfirmationsPanel } from "@/components/ProviderConfirmationsPanel";
import { ProviderAssignmentsList } from "@/components/provider/ProviderAssignmentsList";
import { SupervisionNotesPanel } from "@/components/SupervisionPanel";
import { ProviderInvoicesPanel } from "@/components/ProviderPortalControls";
import { NotificationSettings } from "@/components/NotificationSettings";

const DESCRIPTIONS = {
  credentials: "Licenses, clearances and expirations",
  assignments: "Your assignment details, documents to read and confirmations",
  confirmations: "Placements waiting on your confirmation",
  invoices: "Your 1099 invoices",
  supervision: "Supervision pairing, notes and sign-offs",
};
const TITLES = { assignments: "My assignments", confirmations: "Confirmations", invoices: "Invoices", supervision: "Supervision" };

export default function ProviderFilePage() {
  const { user } = useAuth();
  const { section } = useParams();
  const [tabs, setTabs] = useState(null);
  const [queue, setQueue] = useState(null);
  useEffect(() => {
    api.get("/providers/me/tabs").then((response) => setTabs(response.data)).catch(() => setTabs(null));
  }, []);
  useEffect(() => {
    if ((section === "supervision" || section === "invoices") && !queue) api.get("/providers/me/work-queue").then((response) => setQueue(response.data)).catch(() => setQueue(null));
  }, [section, queue]);
  const reloadQueue = () => api.get("/providers/me/work-queue").then((response) => setQueue(response.data));
  const allowed = !section || (tabs?.file_links || []).some((link) => link.key === section);

  if (section) {
    return (
      <ProviderShell back="/provider-portal/file" eyebrow="MY FILE" title={TITLES[section] || "My file"} testId={`provider-file-${section}-page`}>
        {tabs && !allowed && <p className="muted" data-testid="provider-file-section-unavailable">This section doesn’t apply to your role.</p>}
        {allowed && section === "assignments" && <ProviderAssignmentsList />}
        {allowed && section === "confirmations" && <ProviderConfirmationsPanel />}
        {allowed && section === "supervision" && (queue ? <SupervisionNotesPanel queue={queue} onReload={reloadQueue} /> : <p className="projection-loading">Loading…</p>)}
        {allowed && section === "invoices" && (queue ? <ProviderInvoicesPanel queue={queue} onReload={reloadQueue} /> : <p className="projection-loading">Loading…</p>)}
      </ProviderShell>
    );
  }
  return (
    <ProviderShell eyebrow="PROVIDER" title="My file" testId="provider-file-page">
      <section className="provider-card" data-testid="provider-profile-card">
        <strong data-testid="provider-profile-name">{user?.first_name} {user?.last_name}</strong>
        <span className="muted" data-testid="provider-profile-line">{tabs?.profile_line || user?.discipline || "Provider"}</span>
        <span className="muted">{user?.email}</span>
      </section>
      {(tabs?.file_links || []).map((link) => (
        <Link key={link.key} className="provider-card provider-link-card" data-testid={`provider-file-${link.key}`} to={link.to}>
          <div><strong>{link.label}</strong><span className="muted">{DESCRIPTIONS[link.key]}</span></div>
          <em>Open ›</em>
        </Link>
      ))}
      <section className="provider-card" data-testid="provider-notifications-card">
        <strong>Notifications</strong>
        <span className="muted">Alerts on this phone for report decisions and assignment changes.</span>
        <NotificationSettings variant="provider" />
      </section>
    </ProviderShell>
  );
}
